import React from 'react';
import { useStyleInjection } from '../../hooks';
import { BREAKPOINTS } from '../../tokens';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  maxVisiblePages?: number;
  className?: string;
}

/**
 * Calculate visible page numbers with ellipsis.
 */
function getPageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  let start = Math.max(2, currentPage - halfVisible);
  let end = Math.min(totalPages - 1, currentPage + halfVisible);

  if (currentPage <= halfVisible + 1) {
    end = Math.min(totalPages - 1, maxVisible - 1);
  }
  if (currentPage >= totalPages - halfVisible) {
    start = Math.max(2, totalPages - maxVisible + 2);
  }

  pages.push(1);

  if (start > 2) {
    pages.push('ellipsis');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < totalPages - 1) {
    pages.push('ellipsis');
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  maxVisiblePages = 5,
  className = '',
}: PaginationProps): React.ReactElement | null {
  useStyleInjection(paginationStyles, 'fmp-pagination-styles');

  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages, maxVisiblePages);

  return (
    <nav
      className={`fmp-pagination ${className}`}
      aria-label="Question list pagination"
      role="navigation"
    >
      <button
        type="button"
        className="fmp-pagination__btn fmp-pagination__btn--prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={disabled || currentPage <= 1}
        aria-label="Go to previous page"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" className="fmp-pagination__icon">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        <span className="fmp-pagination__btn-text">Previous</span>
      </button>

      <div className="fmp-pagination__pages" role="group" aria-label="Page numbers">
        {pages.map((page, index) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="fmp-pagination__ellipsis" aria-hidden="true">
              &hellip;
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={`fmp-pagination__page ${page === currentPage ? 'fmp-pagination__page--active' : ''}`}
              onClick={() => onPageChange(page)}
              disabled={disabled || page === currentPage}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        className="fmp-pagination__btn fmp-pagination__btn--next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={disabled || currentPage >= totalPages}
        aria-label="Go to next page"
      >
        <span className="fmp-pagination__btn-text">Next</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" className="fmp-pagination__icon">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </nav>
  );
}

const paginationStyles = `
  :where(.fmp-widget-scope) .fmp-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--fmp-space-xs);
    margin-top: var(--fmp-space-2xl);
    padding: var(--fmp-space-lg) 0;
  }

  :where(.fmp-widget-scope) .fmp-pagination__btn {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
    padding: var(--fmp-space-sm) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    background: var(--fmp-color-surface);
    color: var(--fmp-color-text-secondary);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
    min-height: var(--fmp-size-44);
  }

  :where(.fmp-widget-scope) .fmp-pagination__btn:hover:not(:disabled) {
    border-color: var(--fmp-color-primary);
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-pagination__btn:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-pagination__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-pagination__icon {
    width: var(--fmp-size-16);
    height: var(--fmp-size-16);
  }

  :where(.fmp-widget-scope) .fmp-pagination__pages {
    display: flex;
    align-items: center;
    gap: var(--fmp-size-2);
  }

  :where(.fmp-widget-scope) .fmp-pagination__page {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--fmp-size-40);
    min-height: var(--fmp-size-40);
    padding: var(--fmp-space-xs) var(--fmp-space-sm);
    border: var(--fmp-border-width) solid transparent;
    border-radius: var(--fmp-radius-control);
    background: none;
    color: var(--fmp-color-text-secondary);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-pagination__page:hover:not(:disabled):not(.fmp-pagination__page--active) {
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-pagination__page:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-pagination__page--active {
    background-color: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
    font-weight: var(--fmp-font-weight-semibold);
    cursor: default;
  }

  :where(.fmp-widget-scope) .fmp-pagination__ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--fmp-size-32);
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-base);
  }

  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-pagination__pages {
      display: none;
    }

    :where(.fmp-widget-scope) .fmp-pagination {
      justify-content: space-between;
    }

    :where(.fmp-widget-scope) .fmp-pagination__btn-text {
      display: inline;
    }
  }

  @media screen and (min-width: ${BREAKPOINTS.viewport.mediumMin}) {
    :where(.fmp-widget-scope) .fmp-pagination__btn-text {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-pagination__btn,
    :where(.fmp-widget-scope) .fmp-pagination__page {
      transition: none;
    }
  }

  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-pagination__page--active {
      border: var(--fmp-border-width-strong) solid currentColor;
    }

    :where(.fmp-widget-scope) .fmp-pagination__btn:focus-visible,
    :where(.fmp-widget-scope) .fmp-pagination__page:focus-visible {
      outline-color: currentColor;
    }
  }
`;
